import React, { useEffect } from "react";
import { RiDiscordLine } from "react-icons/ri";
import { RxInstagramLogo } from "react-icons/rx";
import {
  Server,
  Building2,
  LifeBuoy,
  ShieldCheck,
} from "lucide-react";
import { CgYoutube } from "react-icons/cg";

export default function Footer() {
  const footerLinks = {
    services: [
      { name: "Games Hosting", href: "/games" },
      { name: "VPS Hosting", href: "/ryzen-vps" },
      { name: "Storage Servers", href: "/storage" },
    ],
    company: [
      { name: "About Us", href: "/about" },
      { name: "Partners", href: "/partners" },
      {
        name: "Reviews",
        href: "https://uk.trustpilot.com/review/ravenodes.com",
      },
    ],
    support: [
      { name: "Client Area", href: "https://billing.ravenodes.com/" },
      { name: "Service Status", href: "https://status.ravenodes.com" },
      { name: "Game Panel", href: "https://panel.ravenodes.com" },
    ],
    legal: [
      { name: "Terms of Service", href: "/terms-of-service" },
      { name: "Privacy Policy", href: "/privacy-policy" },
      { name: "Data Policy", href: "/gdpr" },
    ],
  };

  const socialLinks = [
    {
      icon: RiDiscordLine,
      href: "https://discord.gg/F9wQ6XwR9g",
      label: "Discord",
    },
    {
      icon: CgYoutube,
      href: "https://www.youtube.com/@ravenodeshosting/shorts",
      label: "YouTube",
    },
    {
      icon: RxInstagramLogo,
      href: "https://www.instagram.com/ravenodeshosting?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
      label: "Instagram",
    },
  ];

  const categoryIcons: Record<string, React.ReactNode> = {
    services: <Server className="text-white" size={23} />,
    company: <Building2 className="text-white" size={23} />,
    support: <LifeBuoy className="text-white" size={23} />,
    legal: <ShieldCheck className="text-white" size={23} />,
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://images.dmca.com/Badges/DMCABadgeHelper.min.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <footer className="bg-gray-900 text-white relative">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-1">
            <a href="/" className="flex items-center space-x-2 mb-2">
              <span className="text-3xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-transparent bg-clip-text">
                RaveNodes
              </span>
            </a>
            <p className="text-gray-300 mb-6 font-semibold">
              Premium game hosting, serving gamers worldwide since 2023.
            </p>
            <div className="flex items-center space-x-4 max-w-fit">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-gray-800/50 transition-all"
                  aria-label={social.label}
                >
                  <social.icon size={22} />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="flex items-center gap-2 text-white text-sm font-bold tracking-wide rounded-lg px-[-1] py-2 w-fit transform translate-y-1">
                <div className="p-2 rounded-md bg-gradient-to-b from-blue-600 to-purple-600 flex items-center justify-center">
                  {categoryIcons[category]}
                </div>
                <span className="capitalize">{category}</span>
              </h3>
              <ul className="mt-2 space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white hover:bg-gray-800/50 transition-all rounded-lg py-1 px-2 -ml-2 inline-block"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-8 border-t border-gray-800/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400">
            Copyright &copy; 2023 - {new Date().getFullYear()} RaveNodes. All
            rights reserved
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a
              href="//www.dmca.com/Protection/Status.aspx?ID=7d814655-088d-4fe7-944c-7a55b9a4a1a8"
              title="DMCA.com Protection Status"
              className="dmca-badge"
            >
              <img
                src="https://images.dmca.com/Badges/dmca-badge-w100-5x1-04.png?ID=7d814655-088d-4fe7-944c-7a55b9a4a1a8"
                alt="DMCA.com Protection Status"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
